// Frontend/src/components/common/UserProfileHeader.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ChevronDown, Users } from 'lucide-react';
import useEmployeeProfile from '../../hooks/useEmployeeProfile';

const UserProfileHeader = ({ profilePath }) => {
  const navigate = useNavigate();
  const employeeProfile = useEmployeeProfile();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleNavigate = (path) => {
    setShowDropdown(false);
    if (path) {
      navigate(path);
    }
  };

  // Extract employee data
  const employeeName = employeeProfile?.Name ||
                       employeeProfile?.displayName ||
                       `${employeeProfile?.FirstName || ''} ${employeeProfile?.LastName || ''}`.trim() ||
                       'User';

  const jobTitle = employeeProfile?.TitleId || 'Employee';
  const personnelNumber = employeeProfile?.PersonnelNumber || employeeProfile?.employeeNo;
  const email = employeeProfile?.PrimaryContactEmail || employeeProfile?.email;

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(employeeName);

  // Show loading state
  if (employeeProfile?.isLoading) {
    return (
      <div className="flex items-center gap-2 sm:gap-3 px-3 py-2">
        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
        <div className="hidden sm:block">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-1"></div>
          <div className="h-3 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        className="flex items-center gap-2 sm:gap-3 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors group"
        title={`${employeeName}${personnelNumber ? ` (${personnelNumber})` : ""}${email ? `\n${email}` : ""}`}
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
          <span className="text-white font-semibold text-sm">{initials}</span>
        </div>
        <div className="hidden sm:block text-right min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">
            {employeeName}
          </p>
          <p className="text-xs text-gray-500 truncate max-w-[150px]">
            {jobTitle}
          </p>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all hidden sm:block ${showDropdown ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />

          {/* Dropdown Content */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-20">
            {/* User Info Section */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white font-semibold text-base">
                    {initials}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {employeeName}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{jobTitle}</p>
                </div>
              </div>
              {personnelNumber && (
                <p className="text-xs text-gray-500 mt-1">
                  ID: {personnelNumber}
                </p>
              )}
              {email && (
                <p className="text-xs text-gray-500 truncate">{email}</p>
              )}
            </div>

            {/* Quick Links */}
            <div className="p-2">
              <button
                onClick={() => handleNavigate(profilePath)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                View Profile
              </button>
              {/* <button
                onClick={() => handleNavigate('/portal/hierarchy')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Organizational Hierarchy
              </button> */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfileHeader;
